import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { HiArrowLeft, HiExternalLink } from 'react-icons/hi';
import data from '../../data/index.json';
import images, { getProjectImage } from '../../constants/images';

const ease = [0.22, 1, 0.36, 1];

const ProjectDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const project = data.portfolio?.find((p) => p.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6">
        <div className="text-center">
          <span className="label">404</span>
          <h1 className="display mt-4">{t('Project Not Found')}</h1>
          <Link to="/" className="btn btn-solid mt-8">
            {t('Return Home')}
          </Link>
        </div>
      </div>
    );
  }

  const { details } = project;
  const canonicalUrl = `https://hyphentech.africa/project/${project.id}`;
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://hyphentech.africa/" },
      { "@type": "ListItem", position: 2, name: "Portfolio", item: "https://hyphentech.africa/#work" },
      { "@type": "ListItem", position: 3, name: project.title, item: canonicalUrl },
    ],
  };

  const facts = [
    { label: t('Project Objectives'), value: details?.objectives },
    { label: t('Key Achievement'), value: details?.achievements },
    { label: t('Strategic Impact'), value: details?.impact },
  ].filter((f) => f.value);

  return (
    <div className="app min-h-screen bg-paper">
      <Helmet>
        <title>{`${project.title} - Hyphen Tech`}</title>
        <meta name="description" content={project.description} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="shell py-12 md:py-16">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="link-underline text-sm font-medium"
        >
          <HiArrowLeft aria-hidden />
          {t('Back to Portfolio')}
        </button>
      </div>

      {/* ── Masthead ── */}
      <header className="shell pb-12 md:pb-16">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="border-t border-[color:var(--rule)] pt-6"
        >
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
            <span className="label tabular text-gold-ink">
              {String(project.id).padStart(2, '0')}
            </span>
            <span className="label">{t('Case Study')}</span>
          </div>

          <h1 className="display-hero mt-6 max-w-[18ch]">{project.title}</h1>

          <p className="lede mt-7">{project.description}</p>

          {project.link && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-solid mt-9"
            >
              {t('Launch Project')}
              <HiExternalLink aria-hidden />
            </a>
          )}
        </motion.div>
      </header>

      {/* ── Plate ── */}
      <motion.figure
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.1, ease }}
        className="shell"
      >
        <div className="overflow-hidden bg-paper-tint aspect-[16/9]">
          <img
            src={getProjectImage(project.src)}
            alt={project.title}
            className="w-full h-full object-cover"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = images.fallbackImage;
            }}
          />
        </div>
      </motion.figure>

      {/* ── Record ── */}
      <div className="shell py-16 md:py-24">
        <div className="max-w-[46rem]">
          {facts.map((fact, i) => (
            <motion.section
              key={fact.label}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.05, ease }}
              className="py-9 border-t border-[color:var(--rule)] last:border-b"
            >
              <h2 className="label">{fact.label}</h2>
              <p className="prose-body mt-4 text-lg">{fact.value}</p>
            </motion.section>
          ))}

          <Link to="/#work" className="btn btn-outline mt-12">
            <HiArrowLeft aria-hidden />
            {t('View more work')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
